import { useCallback, useEffect, useState } from "react";
import type {
  Ad,
  AdType,
  OfferTemplate,
  SearchTargeting,
} from "@b2bportal/media-ads-api";
import { UserDeviceType } from "@b2bportal/media-ads-api";
import { fetchDisplayAds } from "../api/mediaAds";
import { useHtsMediaRequestMetadata } from "../providers/HtsMediaRequestMetadataProvider";

export interface UseMediaAdsOptions {
  /** The placement identifier for the ad slot */
  placementId: string;
  /** The ad type (e.g., DealTile, Banner) */
  adType: AdType;
  /** The country code for user targeting */
  countryCode: string;
  /** Whether the user is on a mobile device */
  isMobile: boolean;
  /** Whether HTS Media is enabled (e.g., via feature flag) */
  enabled: boolean;
  /** Optional key to force a refetch when changed */
  refreshKey?: number;
  /** Search context targeting (e.g. hotel market). Must be referentially stable. */
  search?: SearchTargeting;
}

export type MediaAdsState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "empty" }
  | { status: "loaded"; ads: Ad[]; claimedOffers: OfferTemplate[] };

/**
 * Fetches display ads from the HTS Media API.
 *
 * Returns the raw ads array and any previously claimed offers without
 * imposing interpretation. Consumers decide how to use the data:
 *
 * - **Carousel**: map all `ads` to slides, ignore `claimedOffers`
 * - **Grid with per-ad claim**: render all `ads`, use `useClaimOffer` per tile
 * - **Single banner with claim**: pick `ads.find(a => a.offerTemplate != null)`,
 *   check `claimedOffers` for "already claimed" state
 *
 * @returns An object with `state` representing the current ad state
 * and `reset` to force a fresh fetch cycle.
 */
export const useMediaAds = ({
  placementId,
  adType,
  countryCode,
  isMobile,
  enabled,
  refreshKey = 0,
  search,
}: UseMediaAdsOptions) => {
  const requestMetadata = useHtsMediaRequestMetadata();
  const [state, setState] = useState<MediaAdsState>({ status: "loading" });
  const [internalRefreshKey, setInternalRefreshKey] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setState({ status: "empty" });
      return;
    }

    setState({ status: "loading" });
    const abortController = new AbortController();

    const request = {
      requestMetadata,
      placement: {
        adType,
        placementId,
      },
      targetingParameters: {
        user: {
          country: countryCode,
          deviceType: isMobile
            ? UserDeviceType.MobileWeb
            : UserDeviceType.DesktopWeb,
        },
        search,
      },
    };

    fetchDisplayAds(request, { signal: abortController.signal })
      .then((response) => {
        const data = response.data;
        const claimedOffers = data.claimedOffers ?? [];

        if (data.ads.length === 0 && claimedOffers.length === 0) {
          setState({ status: "empty" });
        } else {
          setState({ status: "loaded", ads: data.ads, claimedOffers });
        }
      })
      .catch((error) => {
        if (!abortController.signal.aborted) {
          console.error("Failed to fetch HTS Media ads", error);
          setState({ status: "error" });
        }
      });

    return () => {
      abortController.abort();
    };
    // `requestMetadata` changes on route change because `pageViewId` is
    // regenerated by PageViewIdProvider. This intentionally triggers a
    // refetch so ads are refreshed for each new page view.
  }, [
    enabled,
    adType,
    isMobile,
    requestMetadata,
    placementId,
    countryCode,
    refreshKey,
    internalRefreshKey,
    search,
  ]);

  const reset = useCallback(() => {
    setInternalRefreshKey((k) => k + 1);
  }, []);

  return { state, reset };
};
